import { graphql, useStaticQuery } from 'gatsby';
import moment from 'moment';
import React, { FunctionComponent, useMemo } from 'react';

import { css } from '@emotion/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { mq } from '@helpers/breakpoints';
import { findMatchingNodePath, isNotEmptyObject } from '@helpers/scripts';
import { Box, Flex, Heading, Text } from '@rebass/emotion';
import { Link } from '@templates/default/components';
import Colors from '@theme/colors';
import { GatsbyImage } from '../component';

interface FooterProps {
  location: {
    pathname: string;
  };
}

const Footer: FunctionComponent<FooterProps> = ({ location }) => {
  const {
    allTrialsJson: { nodes },
  } = useStaticQuery(query);

  // Set defaults for pages that do not have phone or email
  let phone = '';
  let email = '';

  const match = findMatchingNodePath(nodes, location);
  if (isNotEmptyObject(match)) {
    phone = match.phone;
    email = match.email;
  }
  const isSleepActivity: boolean =
    location && location.pathname.includes('/participants/rheumatology/lupus-sjogrens-study');
  const isGatesStudy1: boolean = location && location.pathname.includes('participants/infectiousdisease/COVID19Study');
  const isGatesStudy2: boolean =
    location && location.pathname.includes('participants/infectiousdisease/COVID19Respond');
  const isDCRI: boolean = location && location.pathname.includes('/participants/rheumatology/teen-lupus-study');
  const isFreenome: boolean = location && location.pathname.includes('/participants/oncology/colorectalcancerstudy');
  const isRocheAlectinib: boolean =
    location && location.pathname.includes('/participants/oncology/ALKPositivecancerstudy');
  const copyrightLang: string =
    isSleepActivity || isDCRI || isGatesStudy1 || isGatesStudy2 || isFreenome
      ? `© Science 37, Inc. ${moment().year()} This website is published by Science 37 which is solely responsible for its content. <br> This site is intended for U.S. residents. Last update ${moment().year()}.`
      : `2014-${moment().year()} Science 37, Inc. All Rights Reserved`;
  if (isRocheAlectinib) {
    return (
      <Box style={{ backgroundColor: '#364851', padding: '0 0' }} data-c="footer">
        <Box py={4} width={['95%', '95%', '95%', '80%']} mx="auto">
          <Box width={['220px', '220px', '220px', '220px']} px={'15px'}>
            <GatsbyImage
              css={css`
                height: 100%;
              `}
              imgsrc="s37_rlogo.png"
            />
          </Box>
        </Box>
      </Box>
    );
  }
  if (location && location.href && location.href.match(/sign-up/g)) {
    return <div />;
  }

  if (isDCRI) {
    return (
      <div data-c="footer">
        <Box
          bg={Colors.neutral75}
          css={css`
            position: relative;
            width: 100%;
          `}
        >
          <Box px={'3%'} py={'5%'}>
            <Text fontSize={[1, 1, 2]} textAlign={['left', 'left', 'left', 'center']} className="paragraph">
              <Flex
                css={mq({
                  display: ['inline-grid', 'flex'],
                  textAlign: ['left', 'center'],
                  justifyContent: 'left',
                  flexWrap: 'wrap',
                  lineHeight: ['2em'],
                })}
              >
                <Link href="https://www.science37.com/#vision" text="Vision" color={Colors.neutral0} />
                <Link href="https://www.science37.com/blog/" text="Blog" color={Colors.neutral0} />
                <Link href="https://www.science37.com/careers/" text="Careers" color={Colors.neutral0} />
                <Link
                  href="https://www.science37.com/privacy-policy/"
                  text="Privacy Notice"
                  color={Colors.neutral0}
                  data-c="PrivacyNotice"
                />
                <Link
                  href="https://www.science37.com/terms-conditions/"
                  text="Term & Conditions"
                  color={Colors.neutral0}
                  data-c="Terms-conditions"
                />

                <Flex
                  m={['1em 0 0 1em', '1em  0 0 1em', '1em  0 0 1em', '0', '0']}
                  display={['block', 'flex']}
                  justifyContent={['flex-start', 'flex-start', 'flex-start', 'flex-end', 'flex-end']}
                  flex="auto"
                >
                  <Text color={Colors.science37}>
                    <FontAwesomeIcon
                      icon={['fas', 'comment']}
                      css={mq({
                        color: Colors.science37,
                        margin: '0 10px 0 0',
                      })}
                    />
                    <span style={{ color: '#fff', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {location && location.pathname && location.pathname.includes('/spa') ? 'Spanish' : 'English'}
                    </span>
                  </Text>
                </Flex>
              </Flex>
            </Text>
          </Box>
          <hr
            css={css`
              opacity: 0.13;
            `}
          />
          <Box px={['1%', '3%']} py={['10px', '3%']}>
            <Text fontSize={['5.3pt', 1, 2]} lineHeight={1.5} color={Colors.grayDark8} className="paragraph">
              <div
                dangerouslySetInnerHTML={{ __html: copyrightLang }}
                css={css`
                  color: ${Colors.grayDark8};
                `}
              />
            </Text>
          </Box>
        </Box>
      </div>
    );
  } else {
    return (
      <div data-c="footer">
        <Box
          bg={Colors.neutral75}
          css={css`
            position: relative;
            width: 100%;
          `}
        >
          <Box px={'3%'} py={'5%'}>
            <Text fontSize={[1, 1, 2]} textAlign={['left', 'center']}>
              <Flex
                css={mq({
                  display: ['inline-grid', 'flex'],
                  textAlign: ['left', 'center'],
                  justifyContent: 'left',
                  flexWrap: 'wrap',
                  lineHeight: ['2em'],
                })}
              >
                <Link href="https://www.science37.com/#vision" text="Vision" color={Colors.neutral0} />
                <Link href="https://www.science37.com/blog/" text="Blog" color={Colors.neutral0} />
                <Link href="https://www.science37.com/careers/" text="Careers" color={Colors.neutral0} />
                <Link
                  href="https://www.science37.com/privacy-policy/"
                  text="Privacy Notice"
                  color={Colors.neutral0}
                  data-c="PrivacyNotice"
                />
                <Link
                  href="https://www.science37.com/terms-conditions/"
                  text="Term & Conditions"
                  color={Colors.neutral0}
                  data-c="Terms-conditions"
                />
                {!isGatesStudy1 && !isGatesStudy2 && !isFreenome && (
                  <Flex
                    css={mq({
                      margin: ['1em 0 0 1em', '1em  0 0 1em', '0'],
                      display: ['block', 'flex'],
                      '& a': {
                        display: 'block',
                        marginLeft: 0,
                      },
                    })}
                  >
                    <Heading
                      fontSize={[1, 1, 2]}
                      fontWeight="bold"
                      color={Colors.neutral0}
                      css={mq({
                        textTransform: ['uppercase'],
                        marginTop: ['40px', '0'],
                        marginRight: ['20px'],
                        marginLeft: ['0px', '0px', '14px'],
                      })}
                    >
                      Contact
                    </Heading>
                    <Link href={`tel:${phone}`} text={phone} color={Colors.science37} data-c="phone" />
                    <Link href={`mailto:${email}`} text={email} color={Colors.science37} data-c="email" />
                  </Flex>
                )}
                <Flex
                  m={['1em 0 0 1em', '1em  0 0 1em', '0']}
                  display={['block', 'flex']}
                  justifyContent={['flex-start', 'flex-end']}
                  flex="auto"
                >
                  <Text color={Colors.science37}>
                    <FontAwesomeIcon
                      icon={['fas', 'comment']}
                      css={mq({
                        color: Colors.science37,
                        margin: '0 10px 0 0',
                      })}
                    />
                    <span style={{ color: '#fff', fontWeight: 'bold', textTransform: 'uppercase' }}>
                      {location && location.pathname && location.pathname.includes('/spa') ? 'Spanish' : 'English'}
                    </span>
                  </Text>
                </Flex>
              </Flex>
            </Text>
          </Box>
          <hr
            css={css`
              opacity: 0.13;
            `}
          />
          <Box px={['1%', '3%']} py={['10px', '3%']}>
            <Text fontSize={['5.3pt', 1, 2]} lineHeight={1.5} color={Colors.grayDark8}>
              <div
                dangerouslySetInnerHTML={{ __html: copyrightLang }}
                css={css`
                  color: ${Colors.grayDark8};
                `}
              />
            </Text>
          </Box>
        </Box>
      </div>
    );
  }
};

const query = graphql`
  {
    allTrialsJson {
      nodes {
        path
        phone
        email
      }
    }
  }
`;

export default Footer;
